import React from "react";
import * as Yup from "yup";
import { ErrorMessage, Field, Form, Formik } from "formik";
import classNames from "classnames";
import { Col, Spinner } from "reactstrap";
import FormGenerator from "components/customs/form-generator/input";
import CustomSelect from "components/customs/dropdown";
import { useNavigate } from "react-router";
import { addProjectDropdownStyles } from "constants/style";
import {
  FormActionsSection,
  FormRow,
  RequiredIndicator,
  StyledCancelButton,
  StyledSaveButton,
} from "assets/js/Styles";
import styled from "styled-components";
import {
  baseValidationSchema,
  emailValidationSchema,
  mobileValidationSchema,
} from "validation";
import { POSITION_NATIONAL_AGENCY } from "../constants";

const LastNameFiledContainer = styled(FormRow)`
  margin-top: 0;
`;

const validSchema = baseValidationSchema
  .concat(mobileValidationSchema)
  .concat(emailValidationSchema)
  .shape({
    designation: Yup.string().required("Required"),
    agency: Yup.object().required("Required"),
    area: Yup.object().required("Required"),
  });

const NationalAgencyForm = ({
  handleOnSubmit,
  formFields,
  setFormFields,
  agencies,
  isLoading,
  regions,
}) => {
  const navigate = useNavigate();

  return (
    <Formik
      validationSchema={validSchema}
      initialValues={formFields}
      enableReinitialize
      onSubmit={() => handleOnSubmit()}
    >
      {({ handleChange, values }) => (
        <Form>
          <Field name="lastname">
            {({ form: { touched, errors } }) => (
              <LastNameFiledContainer>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <h5>
                    {"Last Name"}
                    {RequiredIndicator}
                  </h5>
                </Col>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <FormGenerator
                    type="text"
                    name="lastname"
                    className={classNames({
                      "is-invalid": errors?.lastname && touched?.lastname,
                    })}
                    value={values.lastname}
                    onChange={(evt) => {
                      handleChange(evt);

                      setFormFields({
                        ...formFields,
                        lastname: evt.target.value || "",
                      });
                    }}
                  />

                  <ErrorMessage name="lastname">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </LastNameFiledContainer>
            )}
          </Field>

          <Field name="firstname">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <h5>
                    {"First Name"}
                    {RequiredIndicator}
                  </h5>
                </Col>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <FormGenerator
                    type="text"
                    name="firstname"
                    className={classNames({
                      "is-invalid": errors?.firstname && touched?.firstname,
                    })}
                    value={values.firstname}
                    onChange={(evt) => {
                      handleChange(evt);

                      setFormFields({
                        ...formFields,
                        firstname: evt.target.value || "",
                      });
                    }}
                  />
                  <ErrorMessage name="firstname">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <Field name="email">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <h5>
                    {"Email"}
                    {RequiredIndicator}
                  </h5>
                </Col>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <FormGenerator
                    type="text"
                    name="email"
                    className={classNames({
                      "is-invalid": errors?.email && touched?.email,
                    })}
                    value={values.email}
                    onChange={(evt) => {
                      handleChange(evt);

                      setFormFields({
                        ...formFields,
                        email: evt.target.value || "",
                      });
                    }}
                  />
                  <ErrorMessage name="email">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <Field name="mobile">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <h5>
                    {"Contact Number"}
                    {RequiredIndicator}
                  </h5>
                </Col>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <FormGenerator
                    type="mobile"
                    name="mobile"
                    className={classNames({
                      "is-invalid": errors?.mobile && touched?.mobile,
                    })}
                    value={values.mobile || ""}
                    onChange={(value) => {
                      handleChange(value);

                      setFormFields({
                        ...formFields,
                        mobile: value || "",
                      });
                    }}
                  />
                  <ErrorMessage name="mobile">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <Field name="agency">
            {({ form: { touched, errors, setFieldValue } }) => (
              <FormRow>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <h5>
                    {"Agency"}
                    {RequiredIndicator}
                  </h5>
                </Col>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <CustomSelect
                    placeholder=""
                    name="agency"
                    styles={addProjectDropdownStyles}
                    options={agencies}
                    value={values.agency}
                    onChange={(option) => {
                      setFieldValue("agency", option);

                      setFormFields({
                        ...formFields,
                        agency: option,
                      });
                    }}
                    className={classNames({
                      "is-invalid": errors?.agency && touched?.agency,
                    })}
                    filterOption={(option, searchValue) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(searchValue?.toLowerCase()) ||
                      option?.data?.acronym
                        ?.toLowerCase()
                        ?.includes(searchValue?.toLowerCase())
                    }
                  />
                  <ErrorMessage name="agency">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <Field name="area">
            {({ form: { touched, errors, setFieldValue } }) => (
              <FormRow>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <h5>
                    {"Area"}
                    {RequiredIndicator}
                  </h5>
                </Col>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <CustomSelect
                    placeholder=""
                    name="area"
                    styles={addProjectDropdownStyles}
                    options={regions}
                    value={values.area}
                    className={classNames({
                      "is-invalid": errors?.area && touched?.area,
                    })}
                    onChange={(option) => {
                      setFieldValue("area", option);

                      setFormFields({
                        ...formFields,
                        area: option,
                      });
                    }}
                  />
                  <ErrorMessage name="area">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <Field name="designation">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <h5>
                    {"Designation"}
                    {RequiredIndicator}
                  </h5>
                </Col>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <FormGenerator
                    type="text"
                    name="designation"
                    className={classNames({
                      "is-invalid": errors?.designation && touched?.designation,
                    })}
                    value={values.designation}
                    onChange={(evt) => {
                      handleChange(evt);

                      setFormFields({
                        ...formFields,
                        designation: evt.target.value || "",
                      });
                    }}
                  />
                  <ErrorMessage name="designation">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <FormActionsSection>
            <StyledCancelButton
              outline
              disabled={isLoading}
              onClick={() =>
                navigate("/directory", {
                  state: { view: POSITION_NATIONAL_AGENCY },
                })
              }
            >
              {"Cancel"}
            </StyledCancelButton>

            <StyledSaveButton
              type="submit"
              color="primary"
              disabled={isLoading}
            >
              {isLoading ? (
                <Spinner
                  animation="border"
                  color="light"
                  size="sm"
                />
              ) : (
                "Save"
              )}
            </StyledSaveButton>
          </FormActionsSection>
        </Form>
      )}
    </Formik>
  );
};

export default NationalAgencyForm;
